import {
  getBlogPosting,
  getBreadcrumb,
  getJusIaStructuredDataParts,
  getStructuredDataSchema,
  getVideoObject,
  getWebpage,
  organization,
  siteNavigation as sharedSiteNavigation,
  website as sharedWebsite,
} from '@jusbrasil-web/shared-utils-seo'

import { BlogHeadProps } from './blog-head'

const JUSBRASIL_BASE_URL = 'https://www.jusbrasil.com.br'

const getVideoUrl = (articleBody?: string | null): string | undefined => {
  if (!articleBody) return undefined

  const match =
    articleBody.match(/<a[^>]+href=["']([^"']+)["'][^>]*data-media=["']embed-video["'][^>]*>/i) ||
    articleBody.match(/<a[^>]*data-media=["']embed-video["'][^>]+href=["']([^"']+)["'][^>]*>/i)
  return match?.[1]
}

const getImageData = (
  articleBody?: string | null
): { url: string; width?: number; height?: number } | undefined => {
  if (!articleBody) return undefined

  const tagMatch = articleBody.match(/<img[^>]*>/i)
  const imageTag = tagMatch?.[0]
  if (!imageTag) return undefined

  const srcMatch = imageTag.match(/src=["']([^"']+)["']/i)
  const widthMatch = imageTag.match(/width=["']?(\d+)["']?/i)
  const heightMatch = imageTag.match(/height=["']?(\d+)["']?/i)
  const url = srcMatch?.[1]

  if (!url) return undefined

  return {
    url,
    width: widthMatch?.[1] ? Number(widthMatch[1]) : undefined,
    height: heightMatch?.[1] ? Number(heightMatch[1]) : undefined,
  }
}

const toIsoDate = (timestamp?: number | null): string => {
  if (!timestamp) return new Date().toISOString()
  const normalizedTimestamp = timestamp < 1_000_000_000_000 ? timestamp * 1000 : timestamp
  return new Date(normalizedTimestamp).toISOString()
}

export const getStructuredData = ({
  canonicalUrl,
  description,
  title,
  structuredDataContext,
}: Omit<BlogHeadProps, 'imageAlternativeText' | 'keywords'>) => {
  const isJusAcademyPost = !!structuredDataContext?.isJusAcademyPost
  const publishedDate = toIsoDate(structuredDataContext?.publishedAt)
  const videoUrl = getVideoUrl(structuredDataContext?.articleBody)
  const imageData = getImageData(structuredDataContext?.articleBody)
  const authorName = structuredDataContext?.publisher?.fullname || 'Equipe Jusbrasil'
  const authorUrl = structuredDataContext?.publisher?.url || JUSBRASIL_BASE_URL
  const jusIaParts = isJusAcademyPost ? getJusIaStructuredDataParts() : null
  const entityOrganization = jusIaParts?.entityOrganization ?? organization
  const website = jusIaParts?.website ?? sharedWebsite
  const siteNavigation = jusIaParts?.siteNavigation ?? sharedSiteNavigation
  const homeItemUrl = jusIaParts ? `${jusIaParts.website.url}/` : `${JUSBRASIL_BASE_URL}/`

  return getStructuredDataSchema({
    entityOrganization,
    website,
    siteNavigation,
    webpage: {
      ...getWebpage({
        description,
        canonicalUrl,
        name: title,
        image: { name: `${title} Fallback` },
        hasBreadcrumb: true,
      }),
      isPartOf: {
        '@id': `${JUSBRASIL_BASE_URL}/#website`,
      },
      mainEntity: {
        '@id': canonicalUrl,
      },
    },
    breadcrumb: getBreadcrumb({
      canonicalUrl,
      name: title,
      homeItem: { name: 'Home', item: homeItemUrl },
    }),
    blogPosting: getBlogPosting({
      canonicalUrl,
      title,
      description,
      datePublished: publishedDate,
      dateModified: publishedDate,
      author: {
        url: authorUrl,
        name: authorName,
        description: authorName,
      },
      articleBody: structuredDataContext?.articleBody || '',
      image: imageData
        ? {
            url: imageData.url,
            width: imageData.width || 1200,
            height: imageData.height || 675,
          }
        : undefined,
      publisherId: `${JUSBRASIL_BASE_URL}/#organization`,
    }),
    videoObject: videoUrl
      ? getVideoObject({
          canonicalUrl,
          title,
          description,
          uploadDate: publishedDate.slice(0, 10),
          duration: 'PT0M0S',
          contentUrl: videoUrl,
          embedUrl: videoUrl,
          publisherId: `${JUSBRASIL_BASE_URL}/#organization`,
        })
      : undefined,
  })
}
